import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[var(--jb-navy)]/25 py-4 text-[16px] text-[var(--jb-navy)] placeholder:text-[var(--jb-muted)] focus:outline-none focus:border-[var(--jb-rust)] transition-colors";

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[var(--jb-bone)]" data-testid="admin-login">
      <div className="hidden md:block relative bg-[var(--jb-navy)] text-[var(--jb-bone)] p-12 overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative flex flex-col h-full justify-between">
          <div className="flex items-center gap-3 bg-[var(--jb-bone)] p-3 w-fit rounded-md">
            <picture>
              <source srcSet="/logo-jll-full.webp" type="image/webp" />
              <img
                src="/logo-jll-full.png"
                alt="Jaybliss Logistics"
                className="h-16 w-auto object-contain"
                width="448"
                height="320"
                decoding="async"
              />
            </picture>
          </div>
          <div>
            <div className="mono-cap text-[var(--jb-rust)] mb-4">Admin Portal</div>
            <h1 className="serif text-[clamp(40px,5vw,72px)] leading-[0.98]">
              Manage every <br />
              move with <span className="italic">precision.</span>
            </h1>
            <p className="mt-6 text-[var(--jb-bone)]/70 max-w-md">
              Review, confirm and complete customer quote requests from a single command centre.
            </p>
          </div>
          <div className="mono-cap text-[var(--jb-bone)]/50">Belfast · Northern Ireland</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-14">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="mono-cap inline-flex items-center gap-2 text-[var(--jb-muted)] mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
          <div className="mono-cap text-[var(--jb-rust)]">Sign in</div>
          <h2 className="serif text-[44px] leading-tight text-[var(--jb-navy)] mt-3">Admin login.</h2>

          <div className="mt-10 space-y-4">
            <div>
              <label className="mono-cap text-[var(--jb-muted)]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="admin@jayblisslogistics.co.uk"
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="mono-cap text-[var(--jb-muted)]">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-8 py-4 mt-10 w-full sm:w-auto disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {loading ? "Signing in…" : "Sign in →"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
